import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import { createCspmWorkload, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";
import { getComplianceTags as getComplianceTagsApi } from "../../../helpers/auth_aws_helper";
import { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";

const ACCENT = "#F98125";
const FIELD_HEIGHT = 40;

const CLOSE_X_STYLE = {
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  color: "#FFFFFF",
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "none",
  flexShrink: 0,
  WebkitTextStroke: "0.6px #FFFFFF",
};

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    height: FIELD_HEIGHT,
    fontSize: "0.875rem",
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
    fontWeight: 500,
    color: "#111827",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#111827",
  },
};

const SELECT_SX = {
  height: FIELD_HEIGHT,
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cbd5e1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    height: FIELD_HEIGHT,
    boxSizing: "border-box",
    fontWeight: 500,
    color: "#111827",
  },
  "& .MuiSelect-icon": {
    color: "#94a3b8",
  },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
      border: "1px solid #e2e8f0",
      mt: 0.5,
      background: "#fff",
      maxHeight: 300,
      "& .MuiList-root": {
        py: 0.5,
      },
    },
  },
};

const MENU_ITEM_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#111827",
  mx: 0.5,
  my: 0.25,
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#fff4eb",
  },
  "&.Mui-selected": {
    fontWeight: 600,
    color: "#fff",
    backgroundColor: ACCENT,
    "&:hover": {
      backgroundColor: "#e8741c",
      color: "#fff",
    },
  },
};

const AUTOCOMPLETE_POPPER_SX = {
  "& .MuiPaper-root": {
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
    border: "1px solid #e2e8f0",
    mt: 0.5,
  },
  "& .MuiAutocomplete-listbox": {
    py: 0.5,
    fontSize: "0.875rem",
  },
  "& .MuiAutocomplete-option": {
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#111827",
    mx: 0.5,
    my: 0.25,
    borderRadius: "6px",
    minHeight: 36,
    "&[aria-selected='true']": {
      fontWeight: 600,
      color: "#fff",
      backgroundColor: `${ACCENT} !important`,
    },
    "&.Mui-focused": {
      backgroundColor: "#fff4eb",
    },
    "&[aria-selected='true'].Mui-focused": {
      backgroundColor: "#e8741c !important",
      color: "#fff",
    },
  },
};

const INNER_PANEL_SX = {
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  p: 2,
};

const GREY_CLOSE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  color: "#fff",
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#5a6268",
    borderColor: "#5a6268",
    boxShadow: "none",
  },
};

const PRIMARY_BTN_SX = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  backgroundColor: ACCENT,
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#e8741c",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#fed7aa",
    color: "#fff",
  },
};

const CreateWorkloadModal = ({ open, onClose, accounts, onSuccess }) => {
  const dispatch = useDispatch();
  const [selectedAccount, setSelectedAccount] = useState(accounts?.[0]?.accountNumber || "");
  const [workloadName, setWorkloadName] = useState("");
  const [tags, setTags] = useState([{ key: "", value: "" }]);
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const [tagKeysByAccount, setTagKeysByAccount] = useState({});
  const [tagValuesByAccountAndKey, setTagValuesByAccountAndKey] = useState({});
  const [loadingTagKeys, setLoadingTagKeys] = useState(false);
  const [loadingTagValues, setLoadingTagValues] = useState({});
  const [tagApiError, setTagApiError] = useState("");

  const { createCspmWorkloadMessage, createCspmWorkloadError, isCreatingCspmWorkload } = useSelector(
    (state) => state.cspm
  );

  const errors = {
    account: !selectedAccount,
    workloadName: !workloadName.trim(),
    tagKey1: !tags[0].key.trim(),
    tagValue1: !tags[0].value.trim(),
    tagKey2: tags[1] ? !tags[1].key.trim() && !!tags[1].value.trim() : false,
    tagValue2: tags[1] ? !tags[1].value.trim() && !!tags[1].key.trim() : false,
  };
  const hasError = Object.values(errors).some(Boolean);

  const handleBlur = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const handleTagChange = (idx, field, value) => {
    setTags((prev) =>
      prev.map((tag, i) => {
        if (i !== idx) return tag;
        if (field === "key") {
          return { ...tag, key: value, value: "" };
        }
        return { ...tag, [field]: value };
      })
    );
  };

  const handleAddTag = () => {
    if (tags.length < 2) setTags([...tags, { key: "", value: "" }]);
  };

  const handleRemoveTag = () => {
    if (tags.length > 1) setTags(tags.slice(0, 1));
  };

  const handleCreate = () => {
    if (hasError) return;
    const payload = {
      scheduleName: workloadName,
      scheduleType: "on_demand",
      frequency: null,
      resourceScopeType: "tag_based",
      tag: tags
        .filter((tag) => tag.key.trim() && tag.value.trim())
        .map((tag) => ({ key: tag.key.trim(), value: tag.value.trim() })),
    };
    dispatch(createCspmWorkload({ accountNumber: selectedAccount, ...payload }));
  };

  const fetchTagKeys = async (accountNumber) => {
    if (!accountNumber || tagKeysByAccount[accountNumber]) return;
    setLoadingTagKeys(true);
    setTagApiError("");
    try {
      const res = await getComplianceTagsApi({ account_number: accountNumber });
      const keys = Array.isArray(res.keys) ? res.keys : [];
      setTagKeysByAccount((prev) => ({ ...prev, [accountNumber]: keys }));
    } catch (err) {
      setTagApiError(err?.message || "Failed to fetch tag keys");
    } finally {
      setLoadingTagKeys(false);
    }
  };

  const fetchTagValues = async (accountNumber, key) => {
    const cacheKey = `${accountNumber}:${key}`;
    if (!accountNumber || !key || tagValuesByAccountAndKey[cacheKey]) return;
    setLoadingTagValues((prev) => ({ ...prev, [cacheKey]: true }));
    setTagApiError("");
    try {
      const res = await getComplianceTagsApi({ account_number: accountNumber, tag_key: key });
      const values = Array.isArray(res[key]) ? res[key] : [];
      setTagValuesByAccountAndKey((prev) => ({ ...prev, [cacheKey]: values }));
    } catch (err) {
      setTagApiError(err?.message || "Failed to fetch tag values");
    } finally {
      setLoadingTagValues((prev) => ({ ...prev, [cacheKey]: false }));
    }
  };

  useEffect(() => {
    if (open && selectedAccount) {
      fetchTagKeys(selectedAccount);
    }
    // eslint-disable-next-line
  }, [open, selectedAccount]);

  useEffect(() => {
    tags.forEach((tag) => {
      if (tag.key && selectedAccount) {
        fetchTagValues(selectedAccount, tag.key);
      }
    });
    // eslint-disable-next-line
  }, [tags.map((t) => t.key).join(","), selectedAccount]);

  useEffect(() => {
    if (!open) {
      setTagKeysByAccount({});
      setTagValuesByAccountAndKey({});
      setLoadingTagKeys(false);
      setLoadingTagValues({});
      setTagApiError("");
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setSelectedAccount(accounts?.[0]?.accountNumber || "");
      setWorkloadName("");
      setTags([{ key: "", value: "" }]);
      setTouched({});
      setSnackbar({ open: false, message: "", severity: "error" });
      dispatch(clearAllCspmMessages());
    }
    if (!open) {
      setSnackbar({ open: false, message: "", severity: "error" });
      dispatch(clearAllCspmMessages());
    }
    // eslint-disable-next-line
  }, [open, accounts]);

  useEffect(() => {
    if (createCspmWorkloadMessage) {
      if (onSuccess) onSuccess(createCspmWorkloadMessage);
      dispatch(clearAllCspmMessages());
      onClose();
    } else if (createCspmWorkloadError) {
      setSnackbar({ open: true, message: createCspmWorkloadError, severity: "error" });
      dispatch(clearAllCspmMessages());
    }
    // eslint-disable-next-line
  }, [createCspmWorkloadMessage, createCspmWorkloadError]);

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-workload-modal"
        aria-describedby="modal-for-creating-workload"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: ACCENT,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="create-workload-modal"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
                color: "#FFFFFF",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Create Workload
            </Typography>
            <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
              Define a tag-based workload scope for CSPM scans.
            </Typography>
          </Box>
          <button type="button" aria-label="Close" onClick={onClose} style={CLOSE_X_STYLE}>
            <span aria-hidden="true">×</span>
          </button>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "#f3f4f6",
            flex: 1,
            overflow: "auto",
          }}
        >
          <Box sx={{ ...INNER_PANEL_SX, mb: 1.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8125rem",
                color: "#111827",
                mb: 1.5,
              }}
            >
              Workload details
            </Typography>

            <FormControl
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              error={touched.account && errors.account}
            >
              <InputLabel id="account-select-label">Account *</InputLabel>
              <Select
                labelId="account-select-label"
                value={selectedAccount}
                label="Account *"
                onChange={(e) => setSelectedAccount(e.target.value)}
                onBlur={() => handleBlur("account")}
                sx={SELECT_SX}
                MenuProps={SELECT_MENU_PROPS}
              >
                {accounts?.map((acc) => (
                  <MenuItem key={acc.accountNumber} value={acc.accountNumber} sx={MENU_ITEM_SX}>
                    <Box
                      sx={{
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {acc.accountName} ({acc.accountNumber})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {touched.account && errors.account && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                  Account is required
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Workload Name *"
              value={workloadName}
              onChange={(e) => setWorkloadName(e.target.value)}
              onBlur={() => handleBlur("workloadName")}
              fullWidth
              size="small"
              sx={{ ...FIELD_SX, mb: 0.5 }}
              error={touched.workloadName && errors.workloadName}
              helperText={
                touched.workloadName && errors.workloadName ? "Workload Name is required" : ""
              }
              inputProps={{ maxLength: 128 }}
            />
            <Typography
              sx={{
                color: "#64748b",
                fontSize: "0.75rem",
                mt: 0.25,
                display: "block",
                lineHeight: 1.4,
              }}
            >
              A workload name is unique per account.
            </Typography>
          </Box>

          <Box sx={INNER_PANEL_SX}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", color: "#111827" }}>
                Resource tags
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", fontWeight: 500 }}>
                Up to 2 tags
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {tags.map((tag, idx) => {
                const tagKeys = tagKeysByAccount[selectedAccount] || [];
                const cacheKey = `${selectedAccount}:${tag.key}`;
                const tagValues = tagValuesByAccountAndKey[cacheKey] || [];
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "flex-start",
                    }}
                  >
                    <Autocomplete
                      freeSolo
                      options={tagKeys}
                      loading={loadingTagKeys}
                      value={tag.key}
                      onInputChange={(_, newValue) => handleTagChange(idx, "key", newValue)}
                      onBlur={() => handleBlur(`tagKey${idx + 1}`)}
                      fullWidth
                      size="small"
                      componentsProps={{
                        popper: { sx: AUTOCOMPLETE_POPPER_SX },
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                        },
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span
                            style={{
                              display: "inline-block",
                              minWidth: 280,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {option}
                          </span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Tag Key${idx === 0 ? " *" : ""}`}
                          sx={{ ...FIELD_SX, flex: 1, minWidth: 0 }}
                          error={
                            touched[`tagKey${idx + 1}`] &&
                            (idx === 0 ? errors.tagKey1 : errors.tagKey2)
                          }
                          helperText={
                            touched[`tagKey${idx + 1}`] &&
                            (idx === 0
                              ? errors.tagKey1
                                ? "Tag Name is required"
                                : ""
                              : errors.tagKey2
                                ? "Tag Name is required if Tag Value is filled"
                                : "")
                          }
                          inputProps={{ ...params.inputProps, maxLength: 64 }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingTagKeys ? (
                                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Autocomplete
                      freeSolo
                      options={tagValues}
                      loading={!!loadingTagValues[cacheKey]}
                      value={tag.value}
                      onInputChange={(_, newValue) => handleTagChange(idx, "value", newValue)}
                      onBlur={() => handleBlur(`tagValue${idx + 1}`)}
                      fullWidth
                      size="small"
                      componentsProps={{
                        popper: { sx: AUTOCOMPLETE_POPPER_SX },
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                        },
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span
                            style={{
                              display: "inline-block",
                              minWidth: 280,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {option}
                          </span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Tag Value${idx === 0 ? " *" : ""}`}
                          sx={{ ...FIELD_SX, flex: 1, minWidth: 0 }}
                          error={
                            touched[`tagValue${idx + 1}`] &&
                            (idx === 0 ? errors.tagValue1 : errors.tagValue2)
                          }
                          helperText={
                            touched[`tagValue${idx + 1}`] &&
                            (idx === 0
                              ? errors.tagValue1
                                ? "Tag Value is required"
                                : ""
                              : errors.tagValue2
                                ? "Tag Value is required if Tag Name is filled"
                                : "")
                          }
                          inputProps={{ ...params.inputProps, maxLength: 128 }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {!!loadingTagValues[cacheKey] ? (
                                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", pt: 0.5 }}>
                      {idx === 0 && tags.length < 2 ? (
                        <IconButton
                          onClick={handleAddTag}
                          aria-label="Add tag"
                          sx={{
                            color: ACCENT,
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "#fff7ed" },
                          }}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      ) : idx === 1 ? (
                        <IconButton
                          onClick={handleRemoveTag}
                          aria-label="Remove tag"
                          sx={{
                            color: "#ef4444",
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "#fef2f2" },
                          }}
                        >
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      ) : (
                        <IconButton sx={{ opacity: 0, pointerEvents: "none" }}>
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {tagApiError && (
              <Alert
                severity="error"
                onClose={() => setTagApiError("")}
                sx={{ mt: 1.5, borderRadius: "8px" }}
              >
                {tagApiError}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#fff",
            justifyContent: "space-between",
          }}
        >
          <Button variant="contained" disableElevation onClick={onClose} sx={GREY_CLOSE_BTN_SX}>
            Close
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={hasError || isCreatingCspmWorkload}
            onClick={handleCreate}
            startIcon={isCreatingCspmWorkload ? <CircularProgress size={16} color="inherit" /> : null}
            sx={PRIMARY_BTN_SX}
          >
            {isCreatingCspmWorkload ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateWorkloadModal;
